import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { ShieldAlert, Box } from 'lucide-react';
import axios from 'axios';
// --- 🎯 BIMViewer المجهز بالديجيتال توين والبابل التفاعلية ---

const BIMViewer = ({ urn, activeGuidObj, onAssetClick, assetStatuses, isActiveTab }) => {
  const viewerContainer = useRef(null);
  const viewer = useRef(null);
  const isInitializing = useRef(false); // 🎯 قفل لمنع التكرار وتحميل الموديل مرتين
  const bubbleDataRef = useRef(null); // 🎯 مرجع للاحتفاظ ببيانات ومكان البابل

  const [isTreeReady, setIsTreeReady] = useState(false);
  const [isGeometryReady, setIsGeometryReady] = useState(false); 
  
  // =================================================================
  // 1. التهيئة الأساسية للموديل
  // =================================================================
  useEffect(() => {
    if (!urn || viewer.current || isInitializing.current) return;
    
    isInitializing.current = true;
    const Autodesk = window.Autodesk;

    const options = {
      env: 'AutodeskProduction2',
      api: 'streamingV2',
      getAccessToken: async (callback) => {
        try {
          const res = await axios.get('/api/bim/token');
          callback(res.data.access_token, res.data.expires_in);
        } catch (error) {
          console.error("Failed to fetch APS Token", error);
        }
      }
    };

    Autodesk.Viewing.Initializer(options, () => {
      viewer.current = new Autodesk.Viewing.GuiViewer3D(viewerContainer.current);
      viewer.current.start();
      
      const documentId = 'urn:' + urn;
      Autodesk.Viewing.Document.load(documentId, (doc) => {
        const viewables = doc.getRoot().getDefaultGeometry();
        
        // 🎯 الاعتماد على بناء شجرة البيانات وليس فقط الرسمة
        viewer.current.addEventListener(Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT, () => {
          console.log("✅ شجرة البيانات اكتملت. الموديل جاهز للتفاعل!");
          setIsTreeReady(true);
        });
        
        viewer.current.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, () => {
          console.log("✅ المجسمات اكتملت. الموديل جاهز للتلوين!");
          setIsGeometryReady(true);
        });

        viewer.current.loadDocumentNode(doc, viewables).then(() => {
          viewer.current.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, (event) => {
            const dbId = event.dbIdArray[0];
            if (dbId && onAssetClick) {
              viewer.current.getProperties(dbId, (props) => {
                onAssetClick({ guid: props.externalId, dbId: dbId, name: props.name });
              });
            }
          });
        });
      });
    });

    return () => {
      if (viewer.current) {
        viewer.current.finish();
        viewer.current = null;
        isInitializing.current = false;
      }
    };
  }, [urn, onAssetClick]);

  // تحديث أبعاد الموديل عند فتح التابة لتجنب الشاشة السوداء
  useEffect(() => {
    if (isActiveTab && viewer.current) viewer.current.resize();
  }, [isActiveTab]);

  // =================================================================
  // 2. السحر الثاني: الزووم والإضاءة عند التحديد
  // =================================================================
  useEffect(() => {
    if (!isTreeReady || !viewer.current || !activeGuidObj?.guid || !window.THREE || !isActiveTab) return;
    
    const v = viewer.current;
    const targetGuid = activeGuidObj.guid;

    const delayZoom = setTimeout(() => {
      v.resize();
      
      const handleFocus = (dbId) => {
        v.fitToView([dbId]); 
        v.select([dbId]); 
        setTimeout(() => v.clearSelection(), 2000); 

        // 🎯 السحر هنا: بعد ما يخلص زووم، نضرب إيفينت إظهار البابل بالبيانات
        setTimeout(() => {
          const payload = {
              guid: targetGuid,
              assetnum: activeGuidObj.assetnum || '---',
              status: activeGuidObj.status || 'OPERATING',
              meters: activeGuidObj.meters || [],
              stats: { total_wo: 0, has_active_wo: false, active_wo_num: null }
          };
          window.dispatchEvent(new CustomEvent('showAssetBubble', { detail: payload }));
        }, 1200); // تأخير 1.2 ثانية لحد ما الكاميرا توصل
      };

      v.model.getExternalIdMapping((mapping) => {
        const dbId = mapping[targetGuid];
        if (dbId) {
          handleFocus(dbId);
        } else {
          v.search(targetGuid, (dbIds) => {
            if (dbIds && dbIds.length > 0) handleFocus(dbIds[0]);
          }, (err) => console.error(err), ['externalId', 'name', 'label']);
        }
      });
    }, 300);

    return () => clearTimeout(delayZoom);
  }, [activeGuidObj, isTreeReady, isActiveTab]);

  // =================================================================
  // 3. السحر الثالث: التلوين الشامل لكل الحالات
  // =================================================================
  useEffect(() => {
    // 🎯 1. شيلنا !window.THREE من هنا نهائياً، وضفنا !viewer.current.model للأمان
    if (!isGeometryReady || !viewer.current || !viewer.current.model || !assetStatuses) return;
    
    const v = viewer.current;
    
    // 🎯 2. السحب الصحيح لمكتبة THREE من أوتوديسك
    const THREE = window.Autodesk?.Viewing?.THREE || window.THREE;
    if (!THREE) return;

    v.clearThemingColors(v.model);

    v.model.getExternalIdMapping((mapping) => {
        assetStatuses.forEach(assetStatus => {
            const dbId = mapping[assetStatus.guid];
            console.log("Coloring asset:", assetStatus.status); // عشان تتأكد إنها دخلت اللوب
            if (dbId) {
                let color;
                switch(assetStatus.status) {
                    case 'BROKEN': color = new THREE.Vector4(1, 0, 0, 0.7); break;
                    case 'DECOMMISSIONED': color = new THREE.Vector4(0.2, 0.2, 0.2, 0.8); break;
                    case 'INACTIVE': color = new THREE.Vector4(0.6, 0.6, 0.6, 0.7); break;
                    case 'MISSING': color = new THREE.Vector4(0.5, 0, 0.5, 0.7); break;
                    case 'SEALED': color = new THREE.Vector4(0.6, 0.3, 0, 0.7); break;
                    case 'NOT READY': color = new THREE.Vector4(1, 0.6, 0, 0.7); break;
                    case 'ACTIVE': color = new THREE.Vector4(0, 0.8, 0.8, 0.7); break;
                    case 'LIMITED USE': color = new THREE.Vector4(0, 0, 1, 0.7); break;
                    case 'OPERATING': color = new THREE.Vector4(0, 1, 0, 0.7); break;
                    case 'WARNING': color = new THREE.Vector4(1, 0.5, 0, 0.7); break;
                    default: return;
                }
                v.setThemingColor(dbId, color);
            }
        });
    });
  }, [assetStatuses, isGeometryReady]);

  // =================================================================
  // 4. دالة رسم البابل (مُحصنة بـ Inline CSS)
  // =================================================================
  const renderBubble = useCallback(() => {
    if (!bubbleDataRef.current || !viewer.current || !isActiveTab) return;
    
    const { data, center3D } = bubbleDataRef.current;
    const screenPoint = viewer.current.worldToClient(center3D);
    
    if (!screenPoint) return;

    let bubble = document.getElementById(`bubble-${data.guid}`);
    if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = `bubble-${data.guid}`;
        // 🎯 نربط البابل بكونتينر أوتوديسك عشان متستخباش ورا الكانفاس
        viewer.current.container.appendChild(bubble);
    }

    // تصميم محصن
    bubble.style.position = 'absolute';
    bubble.style.zIndex = '999999'; 
    bubble.style.left = `${screenPoint.x}px`;
    bubble.style.top = `${screenPoint.y - 20}px`; 
    bubble.style.transform = 'translate(-50%, -100%)'; 
    bubble.style.pointerEvents = 'none'; 
    bubble.style.transition = 'left 0.05s, top 0.05s'; 
    
    const borderColor = data.status === 'BROKEN' ? '#dc2626' : '#22c55e';
    bubble.style.borderLeft = `5px solid ${borderColor}`;
    bubble.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
    bubble.style.backdropFilter = 'blur(4px)';
    bubble.style.padding = '12px';
    bubble.style.borderRadius = '8px';
    bubble.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.2)';
    bubble.style.minWidth = '160px';
    bubble.style.fontFamily = 'sans-serif';

    // الإخفاء لو الكاميرا لفت الناحية التانية
    if (screenPoint.z > 1) {
        bubble.style.display = 'none';
        return;
    } else {
        bubble.style.display = 'block';
    }

    const totalWo = data.stats?.total_wo || 0;
    const hasActiveWo = data.stats?.has_active_wo || false;
    const activeWoNum = data.stats?.active_wo_num || '';
    const metersHtml = (data.meters || []).map(m => `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
            <span style="font-size: 10px; color: #6b7280; font-weight: bold;">${m.name}:</span>
            <span style="font-size: 11px; font-weight: 900; color: ${m.value > 50 ? '#dc2626' : '#16a34a'}">
                ${m.value} ${m.uom}
            </span>
        </div>
    `).join('');

    bubble.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 6px;">
            <span style="font-size: 12px; font-weight: 900; color: #1e293b;">${data.assetnum}</span>
            <span style="font-size: 9px; padding: 2px 6px; background-color: #dbeafe; color: #1e40af; border-radius: 4px; font-weight: bold;">LIVE</span>
        </div>
        ${metersHtml}
        <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; display: flex; flex-direction: column; gap: 4px;">
            <div style="display: flex; justify-content: space-between; font-size: 10px;">
                <span style="color: #9ca3af; font-weight: bold;">Total WOs:</span>
                <span style="font-weight: 900; color: #334155;">${totalWo}</span>
            </div>
            ${hasActiveWo ? `
                <div style="display: flex; align-items: center; gap: 4px; font-size: 10px; color: #ea580c; font-weight: 900;">
                    <span style="width: 6px; height: 6px; background-color: #ea580c; border-radius: 50%;"></span>
                    Active WO: ${activeWoNum}
                </div>
            ` : '<div style="font-size: 10px; color: #9ca3af; font-style: italic;">No active maintenance</div>'}
        </div>
    `;
  }, [isActiveTab]);

  // =================================================================
  // 5. الاستماع لحركة الكاميرا لتحديث مكان البابل
  // =================================================================
  useEffect(() => {
    if (!viewer.current) return;
    const onCameraChange = () => {
        if (bubbleDataRef.current) renderBubble();
    };
    viewer.current.addEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
    return () => {
        if (viewer.current) viewer.current.removeEventListener(window.Autodesk.Viewing.CAMERA_CHANGE_EVENT, onCameraChange);
    }
  }, [isTreeReady, renderBubble]);

  // =================================================================
  // 6. الاستماع لإشارة السوكيت وحساب الـ BoundingBox
  // =================================================================
  useEffect(() => {
    if (!isTreeReady || !viewer.current) return; // 🎯 شيلنا الاعتماد على window.THREE المباشر
    
    const handleShowBubble = (e) => {
        const data = e.detail;
        const v = viewer.current;
        console.log("🫧 أمر رسم البابل وصل للأصل:", data.assetnum);
        
        const THREE = window.THREE || window.Autodesk.Viewing.THREE;
        if (!THREE) {
            console.error("⚠️ مكتبة THREE لم تحمل بعد!");
            return;
        }
        
        v.model.getExternalIdMapping((mapping) => {
            const dbId = mapping[data.guid];
            if (!dbId) {
                console.warn("⚠️ الأصل مش موجود في الفهرس المباشر للبابل!");
                return;
            }

            const tree = v.model.getInstanceTree();
            const fragList = v.model.getFragmentList();
            const bbox = new THREE.Box3();

            tree.enumNodeFragments(dbId, (fragId) => {
                const fragBox = new THREE.Box3();
                fragList.getWorldBounds(fragId, fragBox);
                bbox.union(fragBox);
            }, true);

            const center3D = new THREE.Vector3(
                (bbox.min.x + bbox.max.x) / 2,
                (bbox.min.y + bbox.max.y) / 2,
                bbox.max.z 
            );

            console.log("📍 إحداثيات البابل في الـ 3D:", center3D);

            bubbleDataRef.current = { data, center3D };
            renderBubble(); 
        });
    };

    window.addEventListener('showAssetBubble', handleShowBubble);
    return () => {
        window.removeEventListener('showAssetBubble', handleShowBubble);
        if (bubbleDataRef.current) {
            const b = document.getElementById(`bubble-${bubbleDataRef.current.data.guid}`);
            if (b) b.remove();
            bubbleDataRef.current = null;
        }
    };
  }, [isTreeReady, renderBubble]);

  // =================================================================
  // 7. الـ Render (شاشة التحميل + الكونتينر)
  // =================================================================
  if (!urn) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-500">
      <ShieldAlert size={48} className="mb-4 opacity-20" />
      <p className="font-bold uppercase tracking-widest">No 3D Model Linked to this Asset</p>
    </div>
  );

  return (
      <div className="w-full h-full relative">
          {!isTreeReady && urn && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm text-blue-400">
                  <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest animate-pulse">Building Digital Twin Data...</span>
              </div>
          )}
          <div ref={viewerContainer} className="w-full h-full rounded-xl overflow-hidden relative" />
      </div>
  );
};

export default BIMViewer;